import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useGroupLogo } from '../contexts/ThemeContext';
import CityMultiSelect from '../components/CityMultiSelect';
import citiesData from '../data/cities_with_coordinates.json';
import UserAvatar from '../components/common/UserAvatar';
import { API_BASE_URL } from '../lib/apiConfig';
import { apiFetch } from '../lib/apiFetch';

const LS_KEY = 'lastLoginGroupBranding';
const DEFAULT_ACCENT = '#1E5BD8';

interface GroupBranding {
  nome: string;
  cor_primaria: string;
  cor_primaria_rgb: string;
  logo_url: string | null;
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const logoIn = keyframes`
  from { opacity: 0; transform: translateY(4px) scale(0.98); }
  to   { opacity: 1; transform: none; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// ── Logged-in account page wrapper ──────────────────────────────────────────
const AccountContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #f8fafc;
  padding: 40px 20px;
  padding-bottom: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 440px;
  margin: 0 auto;
`;

// ── Login page shell ─────────────────────────────────────────────────────────
const LoginPage = styled.div<{ $tint: string; $remembered: boolean }>`
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  overflow: hidden;
  background: #F4F5F7;
  isolation: isolate;

  /* dotted texture */
  &::before {
    content: '';
    position: absolute; inset: -40px;
    background-image: radial-gradient(circle, rgba(11,18,32,.045) 1px, transparent 1.4px);
    background-size: 22px 22px;
    mask-image: radial-gradient(ellipse 80% 60% at 50% 50%, rgba(0,0,0,.8), transparent 75%);
    -webkit-mask-image: radial-gradient(ellipse 80% 60% at 50% 50%, rgba(0,0,0,.8), transparent 75%);
    z-index: -2;
  }

  /* ambient group color glow */
  &::after {
    content: '';
    position: absolute; inset: 0;
    background:
      radial-gradient(60% 40% at 50% 30%, ${({ $tint }) => $tint}, transparent 70%),
      radial-gradient(40% 30% at 20% 80%, ${({ $tint }) => $tint}, transparent 70%);
    opacity: ${({ $remembered }) => ($remembered ? 1 : 0)};
    transition: opacity .5s ease;
    z-index: -1;
    pointer-events: none;
  }
`;

// Attribution chip above card (RS Mandato branding when group is remembered)
const Attrib = styled.div<{ $visible: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px 6px 12px;
  background: #FFFFFF;
  border: 1px solid #ECEDF0;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: #8B93A6;
  box-shadow: 0 2px 6px -2px rgba(16,24,40,.06);
  margin-bottom: 18px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: ${({ $visible }) => ($visible ? 'none' : 'translateY(6px)')};
  transition: opacity .4s ease, transform .4s ease;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
`;

const AttribLogoWrap = styled.span`
  display: inline-flex;
  align-items: center;
  height: 16px;
  img { height: 16px; width: auto; display: block; }
`;

const AttribDot = styled.span`
  width: 4px; height: 4px; border-radius: 50%; background: #B3B9C5;
`;

// The card itself
const LoginCard = styled.div<{ $accent: string; $remembered: boolean }>`
  width: 100%;
  max-width: 440px;
  background: #FFFFFF;
  border: 1px solid #ECEDF0;
  border-radius: 22px;
  box-shadow: 0 1px 1px rgba(16,24,40,.04), 0 10px 28px -10px rgba(16,24,40,.14), 0 30px 60px -20px rgba(16,24,40,.18);
  padding: 36px 36px 28px;
  position: relative;
  animation: ${fadeIn} 0.3s ease;

  /* thin color accent strip at top of card */
  &::before {
    content: '';
    position: absolute;
    left: 36px; right: 36px; top: 0;
    height: 3px;
    background: ${({ $accent }) => $accent};
    border-radius: 0 0 3px 3px;
    opacity: ${({ $remembered }) => ($remembered ? 1 : 0)};
    transition: opacity .35s;
  }
`;

// Logo area inside the card
const LogoWrap = styled.div<{ $isGroup: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 64px;
  margin: 6px 0 24px;

  img {
    max-height: ${({ $isGroup }) => ($isGroup ? '44px' : '56px')};
    max-width: 100%;
    width: auto;
    object-fit: contain;
    animation: ${logoIn} .5s cubic-bezier(.2,.7,.2,1) both;
  }
`;

const Welcome = styled.div`
  text-align: center;
  margin-bottom: 26px;
`;

const WelcomeTitle = styled.h1`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #0B1220;
  margin: 0;
`;

const GroupNameSpan = styled.span<{ $accent: string }>`
  display: block;
  color: ${({ $accent }) => $accent};
`;

const WelcomeSub = styled.div`
  margin-top: 6px;
  font-size: 13.5px;
  color: #8B93A6;
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PasswordStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: ${slideUp} 0.28s cubic-bezier(.2,.7,.2,1) both;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: #5C6577;
  letter-spacing: .02em;
`;

const ForgotLink = styled.a<{ $accent: string }>`
  margin-left: auto;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $accent }) => $accent};
  cursor: pointer;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const InputField = styled.div<{ $accent: string }>`
  position: relative;
  display: flex;
  align-items: center;
  background: #FFFFFF;
  border: 1px solid #DFE1E6;
  border-radius: 11px;
  transition: border-color .15s, box-shadow .15s;

  &:hover { border-color: #C8CDD6; }

  &:focus-within {
    border-color: ${({ $accent }) => $accent};
    box-shadow: 0 0 0 3px ${({ $accent }) => $accent}20;
  }

  input {
    flex: 1;
    padding: 11px 12px 11px 38px;
    background: transparent;
    border: none;
    outline: none;
    font-size: 14.5px;
    color: #0B1220;
    &::placeholder { color: #B3B9C5; }
    &:disabled { opacity: 0.6; }
  }
`;

const InputIcon = styled.span`
  position: absolute;
  left: 12px;
  color: #8B93A6;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const EyeButton = styled.button`
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  color: #8B93A6;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 4px;
  &:hover { color: #5C6577; }
`;

const SubmitBtn = styled.button<{ $accent: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px 20px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  background: ${({ $accent }) => $accent};
  color: #ffffff;
  transition: filter 0.2s, transform 0.1s;
  width: 100%;
  margin-top: 4px;

  &:hover:not(:disabled) { filter: brightness(1.1); }
  &:active:not(:disabled) { transform: scale(0.99); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const Spinner = styled.span<{ $color?: string }>`
  width: 17px;
  height: 17px;
  border: 2.5px solid rgba(255,255,255,0.35);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: ${spin} 0.65s linear infinite;
  flex-shrink: 0;
`;

const SignupRow = styled.div`
  text-align: center;
  margin-top: 16px;
  font-size: 13px;
  color: #5C6577;
  a, button { color: ${() => DEFAULT_ACCENT}; font-weight: 600; background: none; border: none; cursor: pointer; padding: 0; margin-left: 4px; }
  a:hover, button:hover { text-decoration: underline; }
`;

const TrocarRow = styled.div`
  text-align: center;
  margin-top: 8px;
  font-size: 13px;
  color: #8B93A6;
  button { background: none; border: none; color: #8B93A6; font-weight: 600; cursor: pointer; padding: 0; margin-left: 4px; }
  button:hover { text-decoration: underline; color: #5C6577; }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #dc2626;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
`;

const SuccessMessage = styled.div`
  background: #dcfce7;
  color: #16a34a;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
`;

// ── Re-used in logged-in view ────────────────────────────────────────────────
const AccentInput = styled.input<{ $accent: string }>`
  padding: 11px 14px;
  background: #FAFAFB;
  border: 1.5px solid #DFE1E6;
  border-radius: 10px;
  font-size: 15px;
  color: #0B1220;
  transition: border-color 0.2s, box-shadow 0.2s;
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${({ $accent }) => $accent};
    box-shadow: 0 0 0 3px ${({ $accent }) => $accent}26;
  }
  &::placeholder { color: #B0B7C3; }
  &:disabled { opacity: 0.6; }
`;

const AccentButton = styled.button<{ $accent: string }>`
  margin-top: 4px;
  padding: 13px 20px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: ${({ $accent }) => $accent};
  color: #ffffff;
  transition: filter 0.2s, transform 0.1s;
  width: 100%;

  &:hover:not(:disabled) { filter: brightness(1.12); }
  &:active:not(:disabled) { transform: scale(0.99); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const LoginFooter = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const FooterDivider = styled.span`
  color: #CACED6;
  font-size: 12px;
`;

const LinkBtn = styled.button<{ $accent?: string }>`
  background: none;
  border: none;
  color: ${({ $accent }) => $accent || '#8B93A6'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  opacity: 0.85;
  &:hover { opacity: 1; text-decoration: underline; }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: #64748b;
  margin: 0;
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  padding: 32px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 16px;
  border: 1px solid #e2e8f0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.2s, box-shadow 0.2s;
  
  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.1);
  }
  
  &::placeholder {
    color: #94a3b8;
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 14px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  
  ${props => {
    switch (props.$variant) {
      case 'secondary':
        return `
          background: #f1f5f9;
          color: #475569;
          &:hover {
            background: #e2e8f0;
          }
        `;
      case 'danger':
        return `
          background: #fee2e2;
          color: #dc2626;
          &:hover {
            background: #fecaca;
          }
        `;
      default:
        return `
          background: linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%);
          color: white;
          &:hover {
            background: linear-gradient(135deg, var(--accent-dark) 0%, #1d4ed8 100%);
          }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TextButton = styled.button`
  background: none;
  border: none;
  color: var(--accent);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    text-decoration: underline;
  }
`;

const LinkSection = styled.div`
  text-align: center;
  margin-top: 16px;
  font-size: 14px;
  color: #64748b;
`;

const InfoCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
`;

const InfoCardTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
`;

const InfoCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 14px;
  color: #64748b;

  &:not(:last-child) {
    border-bottom: 1px solid #e2e8f0;
  }

  strong {
    color: #1e293b;
  }
`;

const NivelBadge = styled.span<{ nivel: string }>`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    if (props.nivel === 'OURO') return '#fef3c7';
    if (props.nivel === 'PRATA') return '#e5e7eb';
    return '#fed7aa';
  }};
  color: ${props => {
    if (props.nivel === 'OURO') return '#92400e';
    if (props.nivel === 'PRATA') return '#374151';
    return '#9a3412';
  }};
`;

const Divider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: 16px 0;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
  margin-bottom: 32px;
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 32px;
  font-weight: 700;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
`;

const UserDetails = styled.div`
  flex: 1;
  width: 100%;
`;

const UserEmail = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
`;

const UserStatus = styled.div`
  font-size: 14px;
  color: #16a34a;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #dcfce7;
  padding: 6px 12px;
  border-radius: 20px;
  
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #16a34a;
  }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
  
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-size: 14px;
  color: #64748b;
`;

const InfoValue = styled.span`
  font-size: 14px;
  color: #1e293b;
  font-weight: 500;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 32px;
  background: #f1f5f9;
  padding: 6px;
  border-radius: 12px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  
  &::after {
    content: '';
    width: 48px;
    height: 48px;
    border: 4px solid #e2e8f0;
    border-top-color: var(--accent);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 12px;
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.$active ? `
    background: white;
    color: #1e293b;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  ` : `
    background: transparent;
    color: #64748b;
    &:hover {
      color: #1e293b;
    }
  `}
`;

const RegioesGrid = styled.div`
  display: grid;
  gap: 16px;
  margin-top: 24px;
`;

const RegiaoCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: #cbd5e1;
  }
`;

const RegiaoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const RegiaoTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const DeleteButton = styled.button`
  background: transparent;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: #fee2e2;
  }
`;

const CidadesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const CidadeChip = styled.span`
  display: inline-block;
  background: white;
  border: 1px solid #e2e8f0;
  color: #475569;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
  font-size: 14px;
`;

type AuthMode = 'login' | 'register' | 'reset';
type SignupStep = 'code' | 'form';

interface InviteData {
  codigo: string;
  nome_assessor: string;
  nivel_assessor: 'BRONZE' | 'PRATA' | 'OURO';
  valido_ate: string;
  grupo_politico_id: number;
}

interface Regiao {
  id: number;
  nome: string;
  cidade_ids: number[];
  created_at: string;
}

type AccountTab = 'profile' | 'settings';

const Account: React.FC = () => {
  const navigate = useNavigate();
  const groupLogo = useGroupLogo();
  const { user, session, userData, isAdmin, loading, signIn, signUp, signOut, resetPassword, refreshUserData } = useAuth();
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [signupStep, setSignupStep] = useState<SignupStep>('code');

  const [branding, setBranding] = useState<GroupBranding | null>(null);
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [isDefaultMode, setIsDefaultMode] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [loginStep, setLoginStep] = useState<'email' | 'password'>('email');
  const lookupRef = useRef<string>('');
  const [inviteCode, setInviteCode] = useState('');
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Settings states
  const [activeTab, setActiveTab] = useState<AccountTab>('profile');
  const [regioes, setRegioes] = useState<Regiao[]>([]);
  const [loadingRegioes, setLoadingRegioes] = useState(false);
  const [nomeRegiao, setNomeRegiao] = useState('');
  const [cidadesSelecionadas, setCidadesSelecionadas] = useState<number[]>([]);
  const [creatingRegiao, setCreatingRegiao] = useState(false);

  // Redireciona para o mapa se já estiver logado e não estiver na página de configurações
  useEffect(() => {
    if (session && window.location.pathname === '/login') {
      navigate('/mapa', { replace: true });
    }
  }, [session, navigate]);

  // Restore last branding from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(LS_KEY);
    if (stored) {
      try { setBranding(JSON.parse(stored)); } catch { localStorage.removeItem(LS_KEY); }
    }
  }, []);

  const lookupGroup = async (emailValue: string) => {
    if (isDefaultMode) return;
    const trimmed = emailValue.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@') || !trimmed.includes('.')) return;
    if (lookupRef.current === trimmed) return;
    lookupRef.current = trimmed;
    setBrandingLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/grupo-by-email?email=${encodeURIComponent(trimmed)}`);
      const json = await res.json();
      if (json.success && json.data) {
        setBranding(json.data);
        localStorage.setItem(LS_KEY, JSON.stringify(json.data));
      }
    } catch { /* ignore */ } finally {
      setBrandingLoading(false);
    }
  };

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      setError('Por favor, informe um e-mail válido');
      return;
    }
    setError('');
    // Always allow lookup when user explicitly clicks Continuar
    setIsDefaultMode(false);
    lookupRef.current = '';
    await lookupGroup(email);
    setLoginStep('password');
  };

  const resetToDefault = () => {
    setIsDefaultMode(true);
    setBranding(null);
    lookupRef.current = '';
    setLoginStep('email');
    setEmail('');
    setPassword('');
    localStorage.removeItem(LS_KEY);
  };

  // Fetch regioes when logged in as admin
  useEffect(() => {
    if (user && isAdmin && activeTab === 'settings') {
      fetchRegioes();
    }
  }, [user, isAdmin, activeTab]);

  const fetchRegioes = async () => {
    if (!session?.access_token) return;

    setLoadingRegioes(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/regioes`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setRegioes(result.data);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar regiões:', error);
    } finally {
      setLoadingRegioes(false);
    }
  };

  const handleCreateRegiao = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session?.access_token || !nomeRegiao.trim() || cidadesSelecionadas.length === 0) return;

    setCreatingRegiao(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/regioes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nome: nomeRegiao,
          cidade_ids: cidadesSelecionadas
        })
      });

      const result = await response.json();

      if (!result.success) {
        setError(result.error || 'Erro ao criar região');
        setCreatingRegiao(false);
        return;
      }

      setSuccess('Região criada com sucesso!');
      setNomeRegiao('');
      setCidadesSelecionadas([]);
      fetchRegioes();
    } catch (err) {
      setError('Erro ao criar região política');
    } finally {
      setCreatingRegiao(false);
    }
  };

  const handleDeleteRegiao = async (regiaoId: number) => {
    if (!session?.access_token) return;
    if (!window.confirm('Tem certeza que deseja deletar esta região?')) return;

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/regioes/${regiaoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        setSuccess('Região deletada com sucesso');
        fetchRegioes();
      } else {
        setError('Erro ao deletar região');
      }
    } catch (err) {
      setError('Erro ao deletar região política');
    }
  };

  const getCityName = (cityId: number): string => {
    const cities = citiesData as any[];
    const city = cities.find(c => c.id === cityId);
    return city ? `${city.nome} - ${city.microrregiao.mesorregiao.UF.sigla}` : `Cidade #${cityId}`;
  };

  const handleValidateInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/convites/validar/${inviteCode}`);
      const result = await response.json();

      if (!result.success) {
        setError(result.error || 'Código de convite inválido');
        setIsSubmitting(false);
        return;
      }

      setInviteData(result.data);
      setSignupStep('form');
    } catch (err) {
      setError('Erro ao validar código de convite');
    }

    setIsSubmitting(false);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    try {
      if (password !== confirmPassword) {
        setError('As senhas não coincidem');
        setIsSubmitting(false);
        return;
      }
      if (password.length < 6) {
        setError('A senha deve ter pelo menos 6 caracteres');
        setIsSubmitting(false);
        return;
      }

      const response = await apiFetch(`${API_BASE_URL}/api/v1/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          codigo: inviteCode,
          email,
          senha: password,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        setError(result.error || 'Erro ao criar conta');
        setIsSubmitting(false);
        return;
      }

      setSuccess('Conta criada com sucesso! Faça login para continuar.');
      setAuthMode('login');
      setSignupStep('code');
      setInviteCode('');
      setInviteData(null);
      setEmail('');
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError('Ocorreu um erro. Tente novamente.');
    }

    setIsSubmitting(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    try {
      if (authMode === 'login') {
        const { error } = await signIn(email, password);
        if (error) {
          setError(getErrorMessage(error.message));
        } else {
          // Redireciona para o mapa após login bem-sucedido
          navigate('/mapa', { replace: true });
        }
      } else if (authMode === 'reset') {
        const { error } = await resetPassword(email);
        if (error) {
          setError(getErrorMessage(error.message));
        } else {
          setSuccess('E-mail de recuperação enviado! Verifique sua caixa de entrada.');
        }
      }
    } catch (err) {
      setError('Ocorreu um erro. Tente novamente.');
    }

    setIsSubmitting(false);
  };

  const getErrorMessage = (message: string): string => {
    if (message.includes('Invalid login credentials')) {
      return 'E-mail ou senha incorretos';
    }
    if (message.includes('User already registered')) {
      return 'Este e-mail já está cadastrado';
    }
    if (message.includes('Email not confirmed')) {
      return 'Por favor, confirme seu e-mail antes de fazer login';
    }
    if (message.includes('Password should be')) {
      return 'A senha deve ter pelo menos 6 caracteres';
    }
    return message;
  };

  const handleSignOut = async () => {
    await signOut();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const getInitials = (email: string) => {
    return email.charAt(0).toUpperCase();
  };

  if (loading) {
    return (
      <AccountContainer>
        <LoadingSpinner />
      </AccountContainer>
    );
  }

  // Logged in view
  if (user) {
    return (
      <AccountContainer>
        <ContentWrapper style={{ maxWidth: isAdmin ? '800px' : '440px' }}>
          <Header>
            <Title>Minha Conta</Title>
            <Subtitle>Gerencie suas informações de conta</Subtitle>
          </Header>

          {isAdmin && (
            <TabsContainer>
              <Tab $active={activeTab === 'profile'} onClick={() => setActiveTab('profile')}>
                Perfil
              </Tab>
              <Tab $active={activeTab === 'settings'} onClick={() => setActiveTab('settings')}>
                Configurações do Grupo
              </Tab>
            </TabsContainer>
          )}

          {activeTab === 'profile' && (
            <>
              <Card>
                <UserInfo>
                  <UserAvatar
                    name={userData?.nome || user.email || 'U'}
                    fotoUrl={userData?.foto_url}
                    size={80}
                    editable
                    onPhotoUpdated={refreshUserData}
                  />
                  <UserDetails>
                    <UserEmail>{user.email}</UserEmail>
                    <UserStatus>Conta verificada</UserStatus>
                  </UserDetails>
                </UserInfo>

                <Divider />

                <InfoRow>
                  <InfoLabel>E-mail</InfoLabel>
                  <InfoValue>{user.email}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>ID do Usuário</InfoLabel>
                  <InfoValue style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                    {user.id.slice(0, 8)}...
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Membro desde</InfoLabel>
                  <InfoValue>{formatDate(user.created_at)}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Último acesso</InfoLabel>
                  <InfoValue>
                    {user.last_sign_in_at ? formatDate(user.last_sign_in_at) : 'N/A'}
                  </InfoValue>
                </InfoRow>
              </Card>

              <Card>
                <Button $variant="danger" onClick={handleSignOut} style={{ width: '100%' }}>
                  Sair da Conta
                </Button>
              </Card>
            </>
          )}

          {activeTab === 'settings' && isAdmin && (
            <>
              <Card>
                <Header style={{ marginBottom: '24px', textAlign: 'left' }}>
                  <Title style={{ fontSize: '20px', marginBottom: '8px' }}>Regiões Políticas</Title>
                  <Subtitle>Crie regiões agrupando cidades para análise de dados e visualização no mapa</Subtitle>
                </Header>

                {error && <ErrorMessage>{error}</ErrorMessage>}
                {success && <SuccessMessage>{success}</SuccessMessage>}

                <Form onSubmit={handleCreateRegiao}>
                  <InputGroup>
                    <Label htmlFor="nomeRegiao">Nome da Região</Label>
                    <Input
                      id="nomeRegiao"
                      type="text"
                      placeholder="Ex: Região Norte, Zona Sul, etc."
                      value={nomeRegiao}
                      onChange={(e) => setNomeRegiao(e.target.value)}
                      required
                    />
                  </InputGroup>

                  <InputGroup>
                    <Label>Cidades da Região</Label>
                    <CityMultiSelect
                      selectedCityIds={cidadesSelecionadas}
                      onChange={setCidadesSelecionadas}
                      placeholder="Buscar e selecionar cidades..."
                    />
                  </InputGroup>

                  <Button 
                    type="submit" 
                    disabled={creatingRegiao || !nomeRegiao.trim() || cidadesSelecionadas.length === 0}
                  >
                    {creatingRegiao ? 'Criando...' : 'Criar Região'}
                  </Button>
                </Form>

                <Divider style={{ margin: '32px 0' }} />

                <Header style={{ marginBottom: '16px', textAlign: 'left' }}>
                  <Title style={{ fontSize: '18px', marginBottom: '4px' }}>Regiões Criadas</Title>
                  <Subtitle>
                    {loadingRegioes ? 'Carregando...' : `${regioes.length} região${regioes.length !== 1 ? 'ões' : ''}`}
                  </Subtitle>
                </Header>

                {loadingRegioes ? (
                  <LoadingSpinner />
                ) : regioes.length === 0 ? (
                  <EmptyState>
                    Nenhuma região criada ainda.
                    <br />
                    Crie sua primeira região usando o formulário acima.
                  </EmptyState>
                ) : (
                  <RegioesGrid>
                    {regioes.map(regiao => (
                      <RegiaoCard key={regiao.id}>
                        <RegiaoHeader>
                          <RegiaoTitle>{regiao.nome}</RegiaoTitle>
                          <DeleteButton onClick={() => handleDeleteRegiao(regiao.id)}>
                            Deletar
                          </DeleteButton>
                        </RegiaoHeader>
                        <CidadesContainer>
                          {regiao.cidade_ids.slice(0, 10).map(cityId => (
                            <CidadeChip key={cityId}>
                              {getCityName(cityId)}
                            </CidadeChip>
                          ))}
                          {regiao.cidade_ids.length > 10 && (
                            <CidadeChip>
                              +{regiao.cidade_ids.length - 10} cidades
                            </CidadeChip>
                          )}
                        </CidadesContainer>
                      </RegiaoCard>
                    ))}
                  </RegioesGrid>
                )}
              </Card>

              <Card>
                <Button $variant="danger" onClick={handleSignOut} style={{ width: '100%' }}>
                  Sair da Conta
                </Button>
              </Card>
            </>
          )}
        </ContentWrapper>
      </AccountContainer>
    );
  }

  const accent = branding?.cor_primaria || DEFAULT_ACCENT;
  const tint = branding ? `${accent}12` : `rgba(30,91,216,.07)`;
  const remembered = !!branding && !isDefaultMode;

  // ── Small SVG icons ────────────────────────────────────────────────────────
  const IconMail = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/>
    </svg>
  );
  const IconLock = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 1 1 8 0v4"/>
    </svg>
  );
  const IconEye = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  );
  const IconEyeOff = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3l18 18"/><path d="M10.6 6.1A10.9 10.9 0 0 1 12 6c6.5 0 10 6 10 6a17.5 17.5 0 0 1-3.3 4.1M6.7 6.7C3.7 8.5 2 12 2 12s3.5 7 10 7a10.7 10.7 0 0 0 5-1.2"/><path d="M9.5 9.5a3 3 0 0 0 4 4"/>
    </svg>
  );
  const IconArrow = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M13 5l7 7-7 7"/>
    </svg>
  );

  // Login/Register view
  return (
    <LoginPage $tint={tint} $remembered={remembered}>
      {/* RS Mandato attribution chip — visible only when group is remembered */}
      <Attrib $visible={remembered}>
        <AttribLogoWrap><img src="/rs-mandato-logo.png" alt="RS Mandato" /></AttribLogoWrap>
      </Attrib>

      <LoginCard $accent={accent} $remembered={remembered}>
        {authMode === 'register' && signupStep === 'code' ? (
          // ETAPA 1: Validar código de convite
          <Form onSubmit={handleValidateInvite}>
            <Header style={{ marginBottom: '24px' }}>
              <Title style={{ fontSize: '24px' }}>Criar Conta</Title>
              <Subtitle>Informe seu código de convite</Subtitle>
            </Header>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <FieldGroup>
              <FieldLabel htmlFor="inviteCode">Código de Convite</FieldLabel>
              <AccentInput
                id="inviteCode"
                type="text"
                placeholder="Digite o código de 8 caracteres"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                maxLength={8}
                required
                $accent={accent}
                style={{ 
                  fontFamily: 'monospace',
                  textAlign: 'center'
                }}
              />
            </FieldGroup>

            <AccentButton type="submit" disabled={isSubmitting || inviteCode.length !== 8} $accent={accent}>
              {isSubmitting ? 'Validando...' : 'Validar Código'}
            </AccentButton>

            <SignupRow>
              Já possui uma conta?
              <button type="button" onClick={() => setAuthMode('login')}>Fazer login</button>
            </SignupRow>
          </Form>
        ) : authMode === 'register' && signupStep === 'form' ? (
          // ETAPA 2: Preencher dados do cadastro
          <Form onSubmit={handleSignup}>
            <Header style={{ marginBottom: '24px' }}>
              <Title style={{ fontSize: '24px' }}>Completar Cadastro</Title>
              <Subtitle>Preencha seus dados para criar sua conta</Subtitle>
            </Header>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            {inviteData && (
              <InfoCard>
                <InfoCardTitle>Dados do Convite</InfoCardTitle>
                <InfoCardRow>
                  <span>Nome:</span>
                  <strong>{inviteData.nome_assessor}</strong>
                </InfoCardRow>
                <InfoCardRow>
                  <span>Nível:</span>
                  <NivelBadge nivel={inviteData.nivel_assessor}>
                    {inviteData.nivel_assessor}
                  </NivelBadge>
                </InfoCardRow>
              </InfoCard>
            )}

            <FieldGroup>
              <FieldLabel htmlFor="email">E-mail</FieldLabel>
              <AccentInput id="email" type="email" placeholder="seu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required $accent={accent} />
            </FieldGroup>

            <FieldGroup>
              <FieldLabel htmlFor="password">Senha</FieldLabel>
              <AccentInput id="password" type="password" placeholder="Mínimo 6 caracteres" value={password} onChange={(e) => setPassword(e.target.value)} required $accent={accent} />
            </FieldGroup>

            <FieldGroup>
              <FieldLabel htmlFor="confirmPassword">Confirmar Senha</FieldLabel>
              <AccentInput id="confirmPassword" type="password" placeholder="Digite a senha novamente" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required $accent={accent} />
            </FieldGroup>

            <AccentButton type="submit" disabled={isSubmitting} $accent={accent}>
              {isSubmitting ? 'Criando conta...' : 'Criar Conta'}
            </AccentButton>

            <SignupRow>
              <button type="button" onClick={() => { setSignupStep('code'); setInviteData(null); setInviteCode(''); }}>← Voltar para código de convite</button>
            </SignupRow>
          </Form>
        ) : (
          // LOGIN ou RESET
          <LoginForm onSubmit={loginStep === 'email' && authMode !== 'reset' ? handleContinue : handleSubmit} key={authMode}>
            {/* Logo */}
            <LogoWrap $isGroup={remembered}>
              {remembered && branding?.logo_url ? (
                <img src={branding.logo_url} alt={branding.nome} />
              ) : (
                <img src="/rs-mandato-logo.png" alt="RS Mandato" />
              )}
            </LogoWrap>

            {/* Welcome text */}
            <Welcome>
              <WelcomeTitle>
                {remembered && branding ? (
                  <>Bem-vindo de volta<br /><GroupNameSpan $accent={accent}>{branding.nome}</GroupNameSpan></>
                ) : authMode === 'reset' ? (
                  <>Recuperar senha</>
                ) : (
                  <>Entrar no RS Mandato</>
                )}
              </WelcomeTitle>
              <WelcomeSub>
                {remembered
                  ? 'Entre para continuar acompanhando o mandato.'
                  : authMode === 'reset'
                  ? 'Enviaremos um link para redefinir sua senha.'
                  : 'Use seu email para entrar no grupo da sua campanha.'}
              </WelcomeSub>
            </Welcome>

            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}

            {/* Email field — always visible */}
            <FieldGroup>
              <FieldLabel htmlFor="email">Email</FieldLabel>
              <InputField $accent={accent}>
                <InputIcon><IconMail /></InputIcon>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setIsDefaultMode(false);
                    // Going back to email step if user edits
                    if (loginStep === 'password') {
                      setLoginStep('email');
                      setPassword('');
                      lookupRef.current = '';
                    }
                  }}
                  required
                  disabled={isSubmitting}
                />
              </InputField>
            </FieldGroup>

            {/* Step 2: password + submit — slides in only after group lookup finishes */}
            {(loginStep === 'password' || authMode === 'reset') && !brandingLoading && (
              <PasswordStep key="pass">
                {authMode !== 'reset' && (
                  <FieldGroup>
                    <FieldLabel htmlFor="password">
                      Senha
                      <ForgotLink
                        $accent={accent}
                        href="#"
                        onClick={(e) => { e.preventDefault(); setAuthMode('reset'); setError(''); setSuccess(''); }}
                      >
                        Esqueceu?
                      </ForgotLink>
                    </FieldLabel>
                    <InputField $accent={accent}>
                      <InputIcon><IconLock /></InputIcon>
                      <input
                        id="password"
                        type={showPass ? 'text' : 'password'}
                        autoComplete="current-password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={6}
                        disabled={isSubmitting}
                        autoFocus
                      />
                      <EyeButton
                        type="button"
                        aria-label={showPass ? 'Ocultar senha' : 'Mostrar senha'}
                        onClick={() => setShowPass(s => !s)}
                      >
                        {showPass ? <IconEyeOff /> : <IconEye />}
                      </EyeButton>
                    </InputField>
                  </FieldGroup>
                )}

                <SubmitBtn type="submit" disabled={isSubmitting} $accent={accent}>
                  {isSubmitting
                    ? <Spinner />
                    : authMode === 'login'
                    ? <><span>Entrar</span><IconArrow /></>
                    : 'Enviar Link'}
                </SubmitBtn>

                {authMode === 'reset' && (
                  <SignupRow>
                    <button type="button" onClick={() => { setAuthMode('login'); setLoginStep('password'); setError(''); setSuccess(''); }}>← Voltar</button>
                  </SignupRow>
                )}
              </PasswordStep>
            )}

            {/* Step 1: continue button — shows loading spinner while fetching group */}
            {loginStep === 'email' && authMode !== 'reset' && (
              <SubmitBtn type="submit" disabled={brandingLoading} $accent={accent}>
                {brandingLoading ? <Spinner /> : <><span>Continuar</span><IconArrow /></>}
              </SubmitBtn>
            )}

            {/* "Criar conta" always visible in login/email flow */}
            {authMode === 'login' && (
              <SignupRow>
                Novo por aqui?
                <button type="button" onClick={() => { setAuthMode('register'); setSignupStep('code'); setError(''); setSuccess(''); }}>
                  Criar conta
                </button>
              </SignupRow>
            )}

            {remembered && (
              <TrocarRow>
                Não é seu grupo?
                <button type="button" onClick={resetToDefault}>Trocar</button>
              </TrocarRow>
            )}
          </LoginForm>
        )}
      </LoginCard>
    </LoginPage>
  );
};

export default Account;
